<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

type Point = {
  x: number;
  y: number;
};

type Particle = {
  id: number;
  x: string;
  y: string;
  size: string;
  delay: string;
  duration: string;
  drift: string;
  opacity: string;
};

const heroRef = ref<HTMLElement | null>(null);
const visible = ref(false);
const scrollProgress = ref(0);
const target = ref<Point>({ x: 0, y: 0 });
const current = ref<Point>({ x: 0, y: 0 });
let frame = 0;
let scrollFrame = 0;
let observer: IntersectionObserver | null = null;

const particles: Particle[] = [
  { id: 1, x: "9%", y: "14%", size: "0.28rem", delay: "-1.4s", duration: "11s", drift: "10px", opacity: "0.52" },
  { id: 2, x: "18%", y: "32%", size: "0.18rem", delay: "-3.2s", duration: "14s", drift: "8px", opacity: "0.38" },
  { id: 3, x: "28%", y: "9%", size: "0.22rem", delay: "-2.1s", duration: "12s", drift: "12px", opacity: "0.44" },
  { id: 4, x: "42%", y: "24%", size: "0.14rem", delay: "-4.6s", duration: "15s", drift: "16px", opacity: "0.28" },
  { id: 5, x: "55%", y: "12%", size: "0.2rem", delay: "-1.8s", duration: "13s", drift: "14px", opacity: "0.36" },
  { id: 6, x: "66%", y: "30%", size: "0.3rem", delay: "-5.1s", duration: "16s", drift: "18px", opacity: "0.42" },
  { id: 7, x: "77%", y: "18%", size: "0.16rem", delay: "-2.9s", duration: "12s", drift: "9px", opacity: "0.3" },
  { id: 8, x: "86%", y: "38%", size: "0.24rem", delay: "-4s", duration: "15s", drift: "13px", opacity: "0.4" },
  { id: 9, x: "15%", y: "72%", size: "0.2rem", delay: "-3.6s", duration: "14s", drift: "11px", opacity: "0.32" },
  { id: 10, x: "31%", y: "82%", size: "0.14rem", delay: "-5.4s", duration: "17s", drift: "15px", opacity: "0.24" },
  { id: 11, x: "63%", y: "76%", size: "0.22rem", delay: "-2.4s", duration: "13s", drift: "12px", opacity: "0.35" },
  { id: 12, x: "82%", y: "67%", size: "0.18rem", delay: "-1.2s", duration: "11s", drift: "10px", opacity: "0.31" },
];

const highlights = [
  {
    eyebrow: "1",
    title: "Voluntariado corporativo",
    text: "Activaciones de reforestación, limpieza y educación ambiental con impacto visible.",
  },
  {
    eyebrow: "2",
    title: "Huella de carbono",
    text: "Compensación real con proyectos de carbono azul en manglares y costas.",
  },
  {
    eyebrow: "3",
    title: "Patrocinio de impacto",
    text: "Programas para comunidades vulnerables con visibilidad y métricas de resultado.",
  },
] as const;

const cardStats = [
  { value: "ODS", label: "alineación sostenible" },
  { value: "RSE", label: "responsabilidad empresarial" },
  { value: "AZUL", label: "carbono y manglar" },
] as const;

const chapters = [
  { id: "01", label: "Contexto" },
  { id: "02", label: "Territorio" },
  { id: "03", label: "Impacto" },
] as const;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function smoothstep(edge0: number, edge1: number, value: number) {
  const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

function mix(from: number, to: number, t: number) {
  return from + (to - from) * t;
}

const focusText = computed(() => 1 - smoothstep(0.2, 0.56, scrollProgress.value));
const focusImage = computed(() => smoothstep(0.18, 0.62, scrollProgress.value));
const captionReveal = computed(() => smoothstep(0.5, 0.82, scrollProgress.value));
const letterbox = computed(() => smoothstep(0.44, 0.74, scrollProgress.value));

const activeChapter = computed(() => {
  if (scrollProgress.value < 0.3) return 0;
  if (scrollProgress.value < 0.66) return 1;
  return 2;
});

const railInk = computed(() => {
  const t = focusImage.value;
  const r = Math.round(mix(15, 255, t));
  const g = Math.round(mix(23, 255, t));
  const b = Math.round(mix(42, 255, t));
  return `rgba(${r}, ${g}, ${b}, ${mix(0.5, 0.88, t).toFixed(2)})`;
});

const railAccent = computed(() => {
  const t = focusImage.value;
  const r = Math.round(mix(21, 190, t));
  const g = Math.round(mix(128, 242, t));
  const b = Math.round(mix(61, 100, t));
  return `rgb(${r}, ${g}, ${b})`;
});

const sceneStyle = computed(() => ({
  "--mx": current.value.x.toFixed(4),
  "--my": current.value.y.toFixed(4),
  "--scroll": scrollProgress.value.toFixed(4),
  "--focus-text": focusText.value.toFixed(4),
  "--focus-image": focusImage.value.toFixed(4),
  "--caption": captionReveal.value.toFixed(4),
  "--letterbox": letterbox.value.toFixed(4),
  "--rail-ink": railInk.value,
  "--rail-accent": railAccent.value,
}));

function animatePointer() {
  if (frame) return;

  const tick = () => {
    current.value = {
      x: current.value.x + (target.value.x - current.value.x) * 0.085,
      y: current.value.y + (target.value.y - current.value.y) * 0.085,
    };

    const dx = Math.abs(target.value.x - current.value.x);
    const dy = Math.abs(target.value.y - current.value.y);

    if (dx > 0.001 || dy > 0.001) {
      frame = window.requestAnimationFrame(tick);
      return;
    }

    current.value = { ...target.value };
    frame = 0;
  };

  frame = window.requestAnimationFrame(tick);
}

function updateScrollProgress() {
  if (!heroRef.value) return;

  const bounds = heroRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const travel = Math.max(bounds.height - viewportHeight, 1);

  scrollProgress.value = clamp(-bounds.top / travel, 0, 1);
}

function scheduleScrollUpdate() {
  if (scrollFrame) return;

  scrollFrame = window.requestAnimationFrame(() => {
    scrollFrame = 0;
    updateScrollProgress();
  });
}

function onPointerMove(event: PointerEvent) {
  if (!heroRef.value) return;

  const bounds = heroRef.value.getBoundingClientRect();
  const x = (event.clientX - bounds.left) / bounds.width;
  const y = (event.clientY - bounds.top) / bounds.height;

  target.value = {
    x: clamp((x - 0.5) * 2, -1, 1),
    y: clamp((y - 0.5) * 2, -1, 1),
  };
  animatePointer();
}

function onPointerLeave() {
  target.value = { x: 0, y: 0 };
  animatePointer();
}

onMounted(() => {
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        visible.value = true;
      }
    },
    { threshold: 0.15 },
  );

  if (heroRef.value) {
    observer.observe(heroRef.value);
  }

  updateScrollProgress();
  window.addEventListener("scroll", scheduleScrollUpdate, { passive: true });
  window.addEventListener("resize", scheduleScrollUpdate);
});

onBeforeUnmount(() => {
  observer?.disconnect();

  if (frame) {
    window.cancelAnimationFrame(frame);
  }

  if (scrollFrame) {
    window.cancelAnimationFrame(scrollFrame);
  }

  window.removeEventListener("scroll", scheduleScrollUpdate);
  window.removeEventListener("resize", scheduleScrollUpdate);
});
</script>

<template>
  <main class="min-h-screen overflow-x-clip bg-[#edf7ef] text-slate-950">
    <section
      ref="heroRef"
      :style="sceneStyle"
      class="hero-shell relative w-full max-w-none lg:min-h-[350vh]"
      @pointerleave="onPointerLeave"
      @pointermove="onPointerMove"
    >
      <div class="scene-frame sticky top-0 grid min-h-screen grid-cols-1 overflow-hidden lg:block lg:h-screen lg:min-h-0">
        <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(34,197,94,0.18),transparent_24%),radial-gradient(circle_at_80%_15%,rgba(190,242,100,0.16),transparent_20%),radial-gradient(circle_at_50%_100%,rgba(6,95,70,0.14),transparent_36%)]" />
        <div class="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.66),rgba(255,255,255,0.18))]" />
        <div class="pointer-events-none absolute inset-0 grain-overlay" />
        <div class="pointer-events-none absolute inset-0 depth-vignette" />

        <div class="pointer-events-none absolute inset-0 overflow-hidden">
          <span
            v-for="particle in particles"
            :key="particle.id"
            class="particle"
            :style="{
              '--x': particle.x,
              '--y': particle.y,
              '--size': particle.size,
              '--delay': particle.delay,
              '--duration': particle.duration,
              '--drift': particle.drift,
              '--opacity': particle.opacity,
            }"
          />
        </div>

        <div class="scene-text relative flex items-center px-5 py-10 sm:px-8 lg:absolute lg:inset-y-0 lg:left-0 lg:w-[54%] lg:px-12 lg:py-8">
          <div class="pointer-events-none absolute left-[-10%] top-[-10%] h-72 w-72 rounded-full bg-emerald-300/25 blur-3xl soft-orbit" />
          <div class="pointer-events-none absolute bottom-[-12%] right-[-8%] h-80 w-80 rounded-full bg-lime-200/25 blur-3xl soft-orbit delay-2" />

          <div class="content-stage relative w-full max-w-2xl space-y-9">
            <div
              class="reveal-row flex items-center gap-3"
              :class="{ 'is-visible': visible }"
              style="--delay: 0ms;"
            >
              <span class="inline-flex h-3 w-3 rounded-full bg-emerald-600 shadow-[0_0_0_8px_rgba(22,163,74,0.12)] pulse-dot" />
              <p class="text-[0.78rem] font-extrabold uppercase tracking-[0.45em] text-emerald-900/80">
                Sensibilizar.
              </p>
            </div>

            <header class="space-y-6">
              <div class="space-y-4">
                <div
                  class="reveal-row inline-flex w-fit items-center gap-2 rounded-full border border-emerald-900/10 bg-white/60 px-4 py-2 text-[0.8rem] font-semibold tracking-[0.16em] text-emerald-900 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-md"
                  :class="{ 'is-visible': visible }"
                  style="--delay: 120ms;"
                >
                  <span class="h-2 w-2 rounded-full bg-emerald-600" />
                  Educación ambiental con impacto tangible
                </div>

                <h1
                  class="editorial-title reveal-title max-w-[11ch] text-[clamp(3.1rem,5.6vw,5.6rem)] leading-[0.9] tracking-[-0.08em] text-slate-950"
                  :class="{ 'is-visible': visible }"
                  style="--delay: 180ms;"
                >
                  <span class="line"><span>Unete para</span></span>
                  <span class="line"><span><em>educar</em> sobre</span></span>
                  <span class="line"><span>ambiente</span></span>
                </h1>
              </div>

              <div
                class="reveal-row h-1.5 w-24 rounded-full bg-emerald-700 shadow-[0_0_0_10px_rgba(21,128,61,0.08)]"
                :class="{ 'is-visible': visible }"
                style="--delay: 260ms;"
              />

              <p
                class="reveal-row max-w-2xl text-[1.02rem] leading-8 text-slate-700 sm:text-[1.1rem]"
                :class="{ 'is-visible': visible }"
                style="--delay: 320ms;"
              >
                Sensibilizar a la sociedad panameña sobre la importancia de proteger los recursos naturales y los manglares, para promover una cultura de conservación y sostenibilidad.
              </p>
            </header>

            <div class="grid gap-3 sm:grid-cols-3">
              <article
                v-for="(stat, index) in cardStats"
                :key="stat.label"
                class="reveal-stat rounded-[1.2rem] border border-white/70 bg-white/55 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.06)] backdrop-blur-md"
                :class="{ 'is-visible': visible }"
                :style="{ '--delay': `${420 + index * 110}ms` }"
              >
                <p class="text-[0.7rem] font-black uppercase tracking-[0.3em] text-emerald-700/90">{{ stat.value }}</p>
                <p class="mt-2 text-[0.82rem] leading-6 text-slate-600">{{ stat.label }}</p>
              </article>
            </div>

            <div class="space-y-3">
              <article
                v-for="(item, index) in highlights"
                :key="item.title"
                class="reveal-item group flex gap-4 rounded-[1.3rem] border border-transparent bg-transparent p-1 transition-[transform,background,border-color,box-shadow] duration-500 hover:-translate-y-1 hover:border-emerald-900/10 hover:bg-white/50 hover:shadow-[0_24px_70px_rgba(15,23,42,0.08)]"
                :class="{ 'is-visible': visible }"
                :style="{ '--delay': `${540 + index * 110}ms` }"
              >
                <div class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-[0.8rem] font-black text-white transition-transform duration-500 group-hover:scale-110 group-hover:rotate-[-8deg]">
                  {{ item.eyebrow }}
                </div>

                <div class="space-y-1">
                  <h2 class="text-[0.94rem] font-bold text-slate-950">{{ item.title }}</h2>
                  <p class="max-w-2xl text-[0.92rem] leading-7 text-slate-700">{{ item.text }}</p>
                </div>
              </article>
            </div>
          </div>
        </div>

        <aside class="scene-photo relative min-h-[32rem] overflow-hidden lg:absolute lg:inset-0 lg:min-h-0">
          <div class="photo-stage absolute inset-0">
            <img
              src="/assets/images/pachamamaobc_giras_chame_cruzando_puente.jpg"
              alt="Grupo caminando por un puente en una gira ambiental rodeado de vegetación"
              class="photo-image h-full w-full object-cover object-center"
            >

            <div class="image-depth image-depth--one absolute inset-0" />
            <div class="image-depth image-depth--two absolute inset-0" />
            <div class="photo-scrim absolute inset-0" />
            <div class="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,7,18,0.1),rgba(3,7,18,0.02)_30%,rgba(3,7,18,0.5))]" />
            <div class="absolute inset-0 image-sheen" />
            <div class="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-emerald-950/70 to-transparent" />
          </div>

          <div class="scene-ui absolute inset-0 flex flex-col justify-between p-6 sm:p-8">
            <div class="chip-layer flex flex-wrap items-start justify-between gap-3">
              <span class="floating-chip">
                <span class="chip-dot" />
                comunidad y territorio
              </span>
              <span class="floating-chip floating-chip--soft">manglares vivos</span>
            </div>

            <div class="space-y-5">
              <div class="scene-caption max-w-xl">
                <p class="scene-caption__eyebrow">Gira ambiental · Chame</p>
                <h2 class="scene-caption__title editorial-title">
                  <span class="line" style="--lag: 0;"><span>Cada recorrido deja</span></span>
                  <span class="line" style="--lag: 0.12;"><span>una <em>conciencia</em></span></span>
                  <span class="line" style="--lag: 0.24;"><span>que permanece.</span></span>
                </h2>
              </div>

              <div class="grid gap-3 md:grid-cols-2">
                <div class="scene-panel rounded-[1.3rem] border border-white/20 bg-white/12 p-4 text-white shadow-[0_24px_70px_rgba(0,0,0,0.22)] backdrop-blur-2xl" style="--lag: 0;">
                  <p class="text-[0.68rem] font-black uppercase tracking-[0.32em] text-white/70">Impacto</p>
                  <p class="mt-2 text-base font-semibold leading-7">Acciones visibles y medibles para conservación, educación y tejido social.</p>
                </div>

                <div class="scene-panel rounded-[1.3rem] border border-white/20 bg-white/12 p-4 text-white shadow-[0_24px_70px_rgba(0,0,0,0.22)] backdrop-blur-2xl" style="--lag: 0.1;">
                  <p class="text-[0.68rem] font-black uppercase tracking-[0.32em] text-white/70">ODS</p>
                  <p class="mt-2 text-base font-semibold leading-7">Programas alineados con sostenibilidad, comunidad y responsabilidad empresarial.</p>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <div class="letterbox letterbox--top hidden lg:block" />
        <div class="letterbox letterbox--bottom hidden lg:block" />

        <div class="scene-progress hidden lg:block">
          <span class="scene-progress__fill" />
        </div>

        <div class="chapter-rail hidden lg:flex">
          <span class="chapter-rail__label">scroll</span>
          <span
            v-for="(chapter, index) in chapters"
            :key="chapter.id"
            class="chapter"
            :class="{ 'is-active': activeChapter === index }"
          >
            <span class="chapter__text">{{ chapter.id }} · {{ chapter.label }}</span>
            <span class="chapter__dot" />
          </span>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.hero-shell {
  --mx: 0;
  --my: 0;
  --scroll: 0;
  --focus-text: 1;
  --focus-image: 0;
  --caption: 1;
  --letterbox: 0;
  --split: 54%;
  --rail-ink: rgba(15, 23, 42, 0.5);
  --rail-accent: rgb(21, 128, 61);
}

.content-stage {
  transform: translate3d(0, calc(var(--scroll) * -18px), 0);
  will-change: transform;
}

.reveal-row,
.reveal-item,
.reveal-stat {
  opacity: 0;
  transform: translate3d(0, 28px, 0);
  filter: blur(14px);
  transition:
    opacity 820ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 920ms cubic-bezier(0.16, 1, 0.3, 1),
    filter 920ms cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: var(--delay, 0ms);
  will-change: transform, opacity, filter;
}

.reveal-row.is-visible,
.reveal-item.is-visible,
.reveal-stat.is-visible {
  opacity: 1;
  transform: translate3d(0, 0, 0);
  filter: blur(0);
}

.editorial-title {
  font-family: "Cormorant Garamond", serif;
  font-weight: 700;
  letter-spacing: -0.09em;
  text-wrap: balance;
}

.reveal-title {
  perspective: 1000px;
}

.reveal-title .line {
  display: block;
  overflow: hidden;
}

.reveal-title .line > span {
  display: inline-block;
  transform: translate3d(0, 120%, 0) skewY(8deg);
  transform-origin: left center;
  transition: transform 980ms cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: calc(var(--delay, 0ms) + 120ms);
}

.reveal-title.is-visible .line > span {
  transform: translate3d(0, 0, 0) skewY(0deg);
}

.reveal-title em,
.scene-caption em {
  font-style: normal;
  color: #15803d;
}

.scene-caption em {
  color: #bef264;
}

.photo-stage {
  transform:
    translate3d(calc(var(--mx) * -14px), calc(var(--my) * -16px), 0)
    scale(calc(1.06 + var(--scroll) * 0.05));
  transform-origin: center;
  will-change: transform;
}

.photo-image {
  filter: saturate(1.04) contrast(1.03);
}

.photo-scrim {
  background: rgba(2, 12, 8, 0.5);
  opacity: 0;
}

.image-sheen {
  background: linear-gradient(
    115deg,
    transparent 22%,
    rgba(255, 255, 255, 0.2) 38%,
    transparent 54%
  );
  transform: translateX(-120%);
  animation: sheen 10s linear infinite;
  mix-blend-mode: screen;
  opacity: 0.16;
}

.image-depth {
  pointer-events: none;
}

.image-depth--one {
  background: radial-gradient(circle at 72% 18%, rgba(190, 242, 100, 0.18), transparent 26%);
  mix-blend-mode: screen;
  transform: translate3d(calc(var(--mx) * 10px), calc(var(--my) * 10px), 0);
}

.image-depth--two {
  background: radial-gradient(circle at 20% 82%, rgba(5, 150, 105, 0.18), transparent 32%);
  mix-blend-mode: soft-light;
  transform: translate3d(calc(var(--mx) * -8px), calc(var(--my) * -8px), 0);
}

.floating-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(15, 23, 42, 0.2);
  padding: 0.65rem 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  backdrop-filter: blur(22px);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.18);
  transform: translate3d(calc(var(--mx) * 8px), calc(var(--my) * 8px), 0);
}

.floating-chip--soft {
  background: rgba(15, 23, 42, 0.12);
}

.chip-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: #bef264;
  box-shadow: 0 0 24px rgba(190, 242, 100, 0.9);
}

.scene-caption__eyebrow {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

.scene-caption__title {
  margin-top: 0.65rem;
  font-size: clamp(1.9rem, 3vw, 3.1rem);
  line-height: 1.02;
  color: #ffffff;
}

.scene-caption .line {
  display: block;
  overflow: hidden;
}

.scene-caption .line > span {
  display: inline-block;
}

.soft-orbit {
  animation: orbit 18s ease-in-out infinite;
}

.soft-orbit.delay-2 {
  animation-delay: -6s;
}

.pulse-dot {
  animation: pulse 2.8s ease-in-out infinite;
}

.grain-overlay {
  opacity: 0.08;
  background-image:
    linear-gradient(rgba(15, 23, 42, 0.15) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15, 23, 42, 0.15) 1px, transparent 1px);
  background-size: 30px 30px;
  mask-image: radial-gradient(circle at center, black 40%, transparent 100%);
}

.depth-vignette {
  background:
    radial-gradient(circle at center, transparent 0%, transparent 48%, rgba(3, 7, 18, 0.08) 78%, rgba(3, 7, 18, 0.2) 100%),
    linear-gradient(180deg, rgba(2, 6, 23, 0.06), rgba(2, 6, 23, 0.16));
}

.particle {
  position: absolute;
  left: var(--x);
  top: var(--y);
  width: var(--size);
  height: var(--size);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.8);
  opacity: var(--opacity);
  box-shadow: 0 0 18px rgba(190, 242, 100, 0.4);
  animation: particle-float var(--duration) ease-in-out infinite;
  animation-delay: var(--delay);
  transform: translate3d(0, 0, 0);
}

.particle::after {
  content: "";
  position: absolute;
  inset: -10px;
  border-radius: inherit;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.92), transparent 70%);
  opacity: 0.26;
  filter: blur(4px);
}

.letterbox {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 40;
  height: calc(var(--letterbox) * 4rem);
  background: #04120b;
  pointer-events: none;
}

.letterbox--top {
  top: 0;
  box-shadow: inset 0 -1px 0 rgba(190, 242, 100, calc(var(--letterbox) * 0.35));
}

.letterbox--bottom {
  bottom: 0;
  box-shadow: inset 0 1px 0 rgba(190, 242, 100, calc(var(--letterbox) * 0.35));
}

.scene-progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 50;
  background: rgba(15, 23, 42, 0.08);
  pointer-events: none;
}

.scene-progress__fill {
  display: block;
  height: 100%;
  width: 100%;
  transform-origin: left;
  transform: scaleX(var(--scroll));
  background: linear-gradient(90deg, #15803d, #bef264);
}

.chapter-rail {
  position: absolute;
  right: 1.75rem;
  top: 50%;
  z-index: 45;
  flex-direction: column;
  align-items: flex-end;
  gap: 1.1rem;
  transform: translateY(-50%);
  pointer-events: none;
}

.chapter-rail__label {
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: var(--rail-ink);
  writing-mode: vertical-rl;
  margin-bottom: 0.4rem;
}

.chapter {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  color: var(--rail-ink);
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.26em;
  text-transform: uppercase;
}

.chapter__text {
  opacity: 0.35;
  transform: translateX(6px);
  transition:
    opacity 520ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 520ms cubic-bezier(0.16, 1, 0.3, 1);
}

.chapter__dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.35;
  transition:
    transform 520ms cubic-bezier(0.16, 1, 0.3, 1),
    opacity 520ms ease,
    background 520ms ease,
    box-shadow 520ms ease;
}

.chapter.is-active .chapter__text {
  opacity: 1;
  transform: translateX(0);
}

.chapter.is-active .chapter__dot {
  opacity: 1;
  transform: scale(1.5);
  background: var(--rail-accent);
  box-shadow: 0 0 0 6px rgba(190, 242, 100, 0.14);
}

@media (min-width: 1024px) {
  .scene-text {
    z-index: 10;
  }

  .content-stage {
    transform:
      translate3d(calc((1 - var(--focus-text)) * -7%), calc(var(--scroll) * -46px), 0)
      scale(calc(1 - (1 - var(--focus-text)) * 0.07));
    opacity: calc(0.05 + var(--focus-text) * 0.95);
    filter: blur(calc((1 - var(--focus-text)) * 13px));
  }

  .scene-photo {
    z-index: 20;
    clip-path: inset(
      0 0 0 calc(var(--split) - var(--focus-image) * var(--split))
      round calc((1 - var(--focus-image)) * 1.75rem) 0 0 calc((1 - var(--focus-image)) * 1.75rem)
    );
    will-change: clip-path;
  }

  .photo-stage {
    transform:
      translate3d(calc(var(--mx) * -14px), calc(var(--my) * -16px), 0)
      scale(calc(1.14 - var(--focus-image) * 0.09));
    filter:
      blur(calc((1 - var(--focus-image)) * 5px))
      saturate(calc(0.78 + var(--focus-image) * 0.46))
      brightness(calc(0.9 + var(--focus-image) * 0.14));
  }

  .photo-scrim {
    opacity: calc((1 - var(--focus-image)) * 0.75);
  }

  .image-sheen {
    opacity: calc(0.06 + var(--focus-image) * 0.2);
  }

  .scene-ui {
    padding-left: calc(var(--split) - var(--focus-image) * var(--split) + 2.5rem);
    padding-top: calc(2rem + var(--letterbox) * 4rem);
    padding-bottom: calc(2rem + var(--letterbox) * 4rem);
    padding-right: 6.5rem;
  }

  .chip-layer {
    opacity: calc(clamp(0, (var(--focus-image) - 0.08) / 0.35, 1) * (1 - clamp(0, (var(--caption) - 0.35) / 0.45, 1) * 0.85));
    transform: translate3d(0, calc((1 - clamp(0, (var(--focus-image) - 0.08) / 0.35, 1)) * -14px), 0);
  }

  .scene-caption {
    opacity: clamp(0, (var(--caption) - 0.05) / 0.5, 1);
  }

  .scene-caption .line > span {
    --t: clamp(0, (var(--caption) - var(--lag, 0)) / 0.5, 1);
    transform: translate3d(0, calc((1 - var(--t)) * 118%), 0);
  }

  .scene-panel {
    --t: clamp(0, (var(--caption) - 0.2 - var(--lag, 0)) / 0.45, 1);
    opacity: var(--t);
    transform: translate3d(0, calc((1 - var(--t)) * 34px), 0);
  }
}

@keyframes sheen {
  0% {
    transform: translateX(-130%);
  }
  55% {
    transform: translateX(130%);
  }
  100% {
    transform: translateX(130%);
  }
}

@keyframes orbit {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(18px, -22px, 0) scale(1.03);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.22);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 0 0 14px rgba(22, 163, 74, 0);
  }
}

@keyframes particle-float {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(var(--drift), calc(var(--drift) * -1), 0) scale(1.28);
  }
}

/* Desktop con poca altura vertical */
@media (min-width: 1024px) and (max-height: 820px) {

  .scene-text {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }

  /*.content-stage {
    max-width: 38rem;
  }*/
  .content-stage {
    transform:
      translate3d(
        calc((1 - var(--focus-text)) * -7%),
        calc(var(--scroll) * -24px),
        0
      )
      scale(calc(1 - (1 - var(--focus-text)) * 0.07));
  }

  .content-stage {
    gap: 0;
  }

  .content-stage > * + * {
    margin-top: 1.25rem;
  }

  .editorial-title.reveal-title {
    font-size: clamp(2.75rem, 4.4vw, 4.6rem);
    line-height: 0.88;
  }

  .scene-text header {
    gap: 1rem;
  }

  .scene-text header > div {
    gap: 0.75rem;
  }

  .scene-text header > p {
    font-size: 0.92rem;
    line-height: 1.55rem;
  }

  .scene-text .reveal-stat {
    padding: 0.75rem;
  }

  .scene-text .reveal-stat p:last-child {
    margin-top: 0.3rem;
    line-height: 1.15rem;
  }

  .scene-text .reveal-item {
    gap: 0.75rem;
  }

  .scene-text .reveal-item p {
    line-height: 1.3rem;
  }

  .scene-text .reveal-item > div:first-child {
    width: 1.9rem;
    height: 1.9rem;
  }
}

@media (min-width: 1024px) and (max-height: 700px) {

  .scene-text {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  /* .content-stage > * + * {
    margin-top: 0.9rem;
  } */
  .content-stage {
    transform:
      translate3d(
        calc((1 - var(--focus-text)) * -5%),
        calc(var(--scroll) * -14px),
        0
      )
      scale(calc(1 - (1 - var(--focus-text)) * 0.05));
  }

  .editorial-title.reveal-title {
    font-size: clamp(2.4rem, 4vw, 3.8rem);
  }

  .scene-text header > p {
    font-size: 0.86rem;
    line-height: 1.35rem;
  }

  .scene-text .reveal-stat {
    padding: 0.6rem 0.75rem;
  }

  .scene-text .reveal-item {
    padding: 0;
  }

  .scene-text .reveal-item p {
    font-size: 0.8rem;
    line-height: 1.15rem;
  }

  .scene-text .reveal-item h2 {
    font-size: 0.84rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reveal-row,
  .reveal-item,
  .reveal-stat,
  .content-stage,
  .photo-stage,
  .photo-image,
  .image-sheen,
  .soft-orbit,
  .pulse-dot,
  .particle,
  .scene-caption .line > span,
  .scene-panel {
    animation: none !important;
    transition: none !important;
    transform: none !important;
    filter: none !important;
    opacity: 1 !important;
  }

  .scene-photo {
    clip-path: none !important;
  }
}
</style>